import { Link, router, usePage } from '@inertiajs/react'
import { FileText, LogIn, Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import useFeature from '../../../lib/useFeature'
import { useI18n } from '../i18n'
import Brand from './Brand'
import LanguageToggle from './LanguageToggle'
import { surfaceUrl } from './surface-urls'

/**
 * Customer portal lives on a separate host (the `portal.` subdomain of the
 * current host), so the login link is a real cross-surface navigation, not an
 * Inertia visit. Host-relative so it works across dev/staging/production.
 */
const PORTAL_URL = surfaceUrl('portal')

const NAV = [
  { href: '/services', key: 'nav.services' },
  { href: '/about', key: 'nav.about' },
  { href: '/track', key: 'nav.track' },
  { href: '/help', key: 'nav.help' },
  { href: '/contact', key: 'nav.contact' },
] as const

export default function Nav() {
  const { t } = useI18n()
  const { url } = usePage()
  const [menuOpen, setMenuOpen] = useState(false)
  const current = `/${url.replace(/^\//, '').split(/[?#]/)[0]}`
  // Feature-gated public pages (server 404s them when off — hide their links in
  // lockstep, §6.20). States come from Features.public_states via the base
  // controller's `features` share.
  const quoteEnabled = useFeature('module.quote_requests')
  const trackEnabled = useFeature('module.public_tracking')
  const nav = NAV.filter((item) => item.href !== '/track' || trackEnabled)

  // Close the mobile menu on any Inertia navigation, and on Escape.
  useEffect(() => router.on('start', () => setMenuOpen(false)), [])

  useEffect(() => {
    if (!menuOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  return (
    <header className="nav">
      <div className="nav-in">
        <Brand />
        <nav className="nav-links" aria-label={t('a11y.primaryNav')}>
          {nav.map((item) => (
            <Link key={item.href} href={item.href} className={current === item.href ? 'on' : ''}>
              {t(item.key)}
            </Link>
          ))}
        </nav>
        <div className="nav-right">
          <LanguageToggle />
          <a className="chip-btn nav-login" href={PORTAL_URL}>
            <LogIn />
            <span>{t('nav.login')}</span>
          </a>
          {quoteEnabled && (
            <Link className="btn btn-primary nav-cta" href="/quote">
              <FileText />
              <span>{t('nav.quote')}</span>
            </Link>
          )}
          <button
            type="button"
            className="chip-btn icon-only burger"
            aria-label={menuOpen ? t('a11y.closeMenu') : t('a11y.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>

        <div className={menuOpen ? 'mobile-menu on' : 'mobile-menu'}>
          {nav.map((item) => (
            <Link key={item.href} href={item.href}>
              {t(item.key)}
            </Link>
          ))}
          <a className="chip-btn" href={PORTAL_URL} style={{ marginTop: 12 }}>
            <LogIn />
            <span>{t('nav.login')}</span>
          </a>
          {quoteEnabled && (
            <Link className="btn btn-primary btn-block" href="/quote" style={{ marginTop: 10 }}>
              {t('nav.quote')}
            </Link>
          )}
        </div>
      </div>
    </header>
  )
}
